<script setup lang="ts">
import { LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useTodoStream } from '@/composables/useTodoStream'
import { useTicketNotifications } from '@/composables/useTicketNotifications'
import type { Pillar } from '@/composables/useNavigation'

const props = defineProps<{
  pillars: Pillar[]
  activePillarId: string
  isPanelOpen: boolean
}>()

const emit = defineEmits<{
  'select-pillar': [id: string]
  'logout': []
}>()

const authStore = useAuthStore()
const { badgeCount: todoBadgeCount } = useTodoStream()
const { newTicketCount } = useTicketNotifications()

function getPillarBadge(pillar: Pillar): number {
  let total = 0
  for (const item of pillar.items) {
    if (item.badge === 'todo') total += todoBadgeCount.value
    if (item.badge === 'ticket') total += newTicketCount.value
  }
  return total
}
</script>

<template>
  <div class="nav-rail">
    <!-- Logo -->
    <div class="rail-logo">
      <img
        src="/logo-icon-64.png"
        srcset="/logo-icon-64.png 1x, /logo-icon-128.png 2x"
        alt="Vigil Cyber"
        class="w-9 h-9 object-contain"
      />
    </div>

    <!-- Pillar Icons -->
    <nav class="rail-icons">
      <button
        v-for="pillar in props.pillars"
        :key="pillar.id"
        class="rail-icon-btn"
        :class="{ active: props.activePillarId === pillar.id && props.isPanelOpen }"
        :title="pillar.label"
        @click="emit('select-pillar', pillar.id)"
      >
        <component :is="pillar.icon" class="w-6 h-6" />
        <span v-if="getPillarBadge(pillar) > 0" class="rail-badge-dot" />
      </button>
    </nav>

    <!-- User Footer -->
    <div class="rail-footer">
      <router-link to="/account" class="rail-icon-btn" title="My Account">
        <div class="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-semibold">
          {{ authStore.initials }}
        </div>
      </router-link>
      <button class="rail-icon-btn" title="Sign Out" @click="emit('logout')">
        <LogOut class="w-5 h-5" />
      </button>
    </div>
  </div>
</template>
